import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab, Switch, FormControlLabel
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import { useSnackbar } from 'notistack';
import vendedorService, { Vendedor } from '../../services/vendedorService';
import LocalidadSearch from '../../components/LocalidadSearch';
import UsuarioSearch from '../../components/UsuarioSearch';
import GrupoMcpSearch from '../../components/GrupoMcpSearch';
import { Usuario } from '../../services/usuarioService';
import { GrupoMcp } from '../../services/grupoMcpService';

const defaultForm: Vendedor = {
  nroVendedor: 0,
  nombreVendedor: '',
  legajo: '',
  tipoVendedor: 'A',
  comisionPorc: 0,
  comisionBase: 'Neto',
  direccion: '',
  codigoCiudad: 0,
  telefono: '',
  remuneracion: 0,
  notas: '',
  lunes: 0,
  martes: 0,
  miercoles: 0,
  jueves: 0,
  viernes: 0,
  sabado: 0,
  domingo: 0,
  horaElun: null,
  horaSlun: null,
  horaEmar: null,
  horaSmar: null,
  horaEmie: null,
  horaSmie: null,
  horaEjue: null,
  horaSjue: null,
  horaEvie: null,
  horaSvie: null,
  horaEsab: null,
  horaSsab: null,
  horaEdom: null,
  horaSdom: null,
  usuario: '',
  sincronizar: 1,
  grupoMcpMobile: 0
};

const tiposVendedor = [
  { value: 'A', label: 'Activo' },
  { value: 'I', label: 'Inactivo' }
];

const VendedorForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoVendedor } = useParams();
  const [formData, setFormData] = useState<Vendedor>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [localidadSearchOpen, setLocalidadSearchOpen] = useState(false);
  const [usuarioSearchOpen, setUsuarioSearchOpen] = useState(false);
  const [grupoMcpSearchOpen, setGrupoMcpSearchOpen] = useState(false);

  const loadVendedor = useCallback(async () => {
    if (!codigoVendedor || codigoVendedor === 'nuevo') return;
    try {
      setLoading(true);
      const vendedor = await vendedorService.getVendedor(Number(codigoVendedor));
      setFormData(vendedor);
    } catch (error) {
      enqueueSnackbar('Error al cargar el vendedor', { variant: 'error' });
      navigate('/ventas/vendedores');
    } finally {
      setLoading(false);
    }
  }, [codigoVendedor, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codigoVendedor && codigoVendedor !== 'nuevo') {
      loadVendedor();
    }
  }, [codigoVendedor, loadVendedor]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericFields = ['nroVendedor', 'comisionPorc', 'codigoCiudad', 'dni', 'remuneracion', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo', 'turno', 'comisionServicio', 'nroProveedor', 'sincronizar', 'grupoMcpMobile'];
    
    if (numericFields.includes(name)) {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
  };

  const handleLocalidadSelect = (localidad: any) => {
    setFormData(prev => ({
      ...prev,
      codigoCiudad: localidad.codigoCiudad
    }));
    setLocalidadSearchOpen(false);
  };

  const handleUsuarioSelect = (usuario: Usuario) => {
    const usuarioId = String(usuario.usuario || usuario.username || usuario.id || '').trim();
    setFormData(prev => ({
      ...prev,
      usuario: usuarioId
    }));
    setUsuarioSearchOpen(false);
  };

  const handleGrupoMcpSelect = (grupoMcp: GrupoMcp) => {
    setFormData(prev => ({
      ...prev,
      grupoMcpMobile: grupoMcp.grupoMcp
    }));
    setGrupoMcpSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nombreVendedor.trim()) {
        enqueueSnackbar('El nombre del vendedor es requerido', { variant: 'warning' });
        return;
      }

      if (codigoVendedor && codigoVendedor !== 'nuevo') {
        await vendedorService.updateVendedor(Number(codigoVendedor), formData);
      } else {
        await vendedorService.createVendedor(formData);
      }

      enqueueSnackbar('Vendedor guardado exitosamente', { variant: 'success' });
      navigate('/ventas/vendedores');
    } catch (error: any) {
      console.error('Error al guardar vendedor:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el vendedor', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {codigoVendedor && codigoVendedor !== 'nuevo' ? 'Editar Vendedor' : 'Nuevo Vendedor'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Horarios" />
            <Tab label="Comisiones" />
            <Tab label="App Movil" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="nroVendedor" 
                    label="N° Vendedor" 
                    type="number"
                    value={formData.nroVendedor} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!codigoVendedor && codigoVendedor !== 'nuevo'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField 
                    name="nombreVendedor" 
                    label="Nombre" 
                    value={formData.nombreVendedor} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    inputProps={{ maxLength: 20 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="legajo" 
                    label="Legajo" 
                    value={formData.legajo || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ maxLength: 20 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SearchableSelect
                    name="tipoVendedor"
                    label="Tipo Vendedor"
                    value={formData.tipoVendedor || 'A'}
                    onChange={(v) => setFormData(prev => ({ ...prev, tipoVendedor: v ?? 'A' }))}
                    options={tiposVendedor}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="direccion" 
                    label="Dirección" 
                    value={formData.direccion || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ maxLength: 25 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Localidad" 
                      value={formData.codigoCiudad || ''} 
                      fullWidth
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setLocalidadSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="telefono" 
                    label="Teléfono" 
                    value={formData.telefono || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ maxLength: 25 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="notas" 
                    label="Notas" 
                    value={formData.notas || ''} 
                    onChange={handleChange} 
                    fullWidth
                    multiline
                    rows={3}
                    inputProps={{ maxLength: 5000 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Horarios de Trabajo</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaElun" 
                    label="Lunes - Entrada" 
                    type="time"
                    value={formData.horaElun || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaSlun" 
                    label="Lunes - Salida" 
                    type="time"
                    value={formData.horaSlun || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaEmar" 
                    label="Martes - Entrada" 
                    type="time"
                    value={formData.horaEmar || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaSmar" 
                    label="Martes - Salida" 
                    type="time"
                    value={formData.horaSmar || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaEmie" 
                    label="Miércoles - Entrada" 
                    type="time"
                    value={formData.horaEmie || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaSmie" 
                    label="Miércoles - Salida" 
                    type="time"
                    value={formData.horaSmie || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaEjue" 
                    label="Jueves - Entrada" 
                    type="time"
                    value={formData.horaEjue || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaSjue" 
                    label="Jueves - Salida" 
                    type="time"
                    value={formData.horaSjue || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaEvie" 
                    label="Viernes - Entrada" 
                    type="time"
                    value={formData.horaEvie || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaSvie" 
                    label="Viernes - Salida" 
                    type="time"
                    value={formData.horaSvie || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="comisionPorc" 
                    label="Comisión %" 
                    type="number"
                    value={formData.comisionPorc || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ step: '0.01', min: '0', max: '100' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SearchableSelect
                    name="comisionBase"
                    label="Base Comisión"
                    value={formData.comisionBase || 'Neto'}
                    onChange={(v) => setFormData(prev => ({ ...prev, comisionBase: v ?? 'Neto' }))}
                    options={[
                      { value: 'Neto', label: 'Neto' },
                      { value: 'Importe total', label: 'Importe total' },
                    ]}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="remuneracion" 
                    label="Remuneración" 
                    type="number"
                    value={formData.remuneracion || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Configuración App Movil</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Number(formData.sincronizar || 0) === 1}
                        onChange={handleSwitchChange}
                        name="sincronizar"
                        color="primary"
                      />
                    }
                    label="Sincronizar"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      name="usuario"
                      label="Usuario"
                      value={formData.usuario || ''}
                      fullWidth
                      disabled
                      inputProps={{ maxLength: 50 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => setUsuarioSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="GrupoMCP_Mobile"
                      value={formData.grupoMcpMobile || ''}
                      fullWidth
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => setGrupoMcpSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/ventas/vendedores')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <LocalidadSearch
        open={localidadSearchOpen}
        onClose={() => setLocalidadSearchOpen(false)}
        onSelect={handleLocalidadSelect}
      />
      <UsuarioSearch
        open={usuarioSearchOpen}
        onClose={() => setUsuarioSearchOpen(false)}
        onSelect={handleUsuarioSelect}
      />
      <GrupoMcpSearch
        open={grupoMcpSearchOpen}
        onClose={() => setGrupoMcpSearchOpen(false)}
        onSelect={handleGrupoMcpSelect}
      />
    </Box>
  );
};

export default VendedorForm;
